import { useQuery } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { orpc } from '@/api/rpc/client';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { pricelistEventLabel } from './pricelistEventLabels';

/** The pricelist's event history, newest first. */
export function PricelistEventHistory({ documentId }: { documentId: string }) {
  const gt = useGT();
  const eventsQuery = useQuery(
    orpc.inventoryManagement.pricelist.listEvents.queryOptions({
      input: { pdfDocumentId: documentId },
    }),
  );
  const events = eventsQuery.data?.events ?? [];

  return (
    <div className="space-y-2 border-t pt-4">
      <Label className="text-xs text-muted-foreground">
        <T context="pricelist event history section heading">History</T>
      </Label>
      {eventsQuery.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="empty state for the pricelist event history">
            No events yet.
          </T>
        </p>
      ) : (
        <ul className="space-y-2.5">
          {events.map((event) => (
            <li key={event.id} className="border-l-2 pl-2.5">
              <p className="text-xs font-medium text-foreground">
                {pricelistEventLabel(gt, event)}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {event.actorName ??
                  gt('Unknown user', {
                    context:
                      'pricelist history: fallback actor name for a deleted/system user',
                  })}
                {' · '}
                {format(new Date(event.createdAt), 'MMM d, yyyy h:mm a')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
